import { JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { useGlobal } from '~/hooks/useGlobal';

import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import { BreadcrumbsItem } from '../../../stores/CatalogStore';

import MobileHeaderLayout from '../../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../../Main/MobileHeader/MobileHeaderTitle';

import styles from '../Legals.module.scss';

import mdStyles from './LegalsCerise.module.scss';

import '../styles.scss';

type Props = {
  handleLink: (anchor: string) => void;
};

const LEGALS_NAV = [
  {
    link: 'terms-and-conditions',
    title: 'Terms & Conditions',
  },
  {
    link: 'privacy-policy',
    title: 'Privacy Policy',
  },
];

const termsContent = {
  en: `
<h2 id="terms-and-conditions">Terms & Conditions</h2>

**Effective Date:** February 25, 2025

## Acceptance
By using cerise.ga, you implicitly accept these terms of use.

## Service Description
Cerise.ga offers an online service that allows users in Libreville to order grocery products delivered in less than 4 hours.

## Payments and Refunds
Payments are processed through our standard online payment system. Refunds follow the usual policy of e-commerce grocery applications.

## Account Termination
Cerise.ga reserves the right to suspend or terminate a user account in case of fraud or suspicious activities.

## Intellectual Property
The Cerise.ga website and its content are the exclusive property of Cerise.ga or its suppliers.

## Applicable Laws
These terms are exclusively governed by the laws of Gabon.

## Changes to Terms
Cerise.ga reserves the right to modify these terms of use at any time. Any changes will take effect immediately upon publication on our site.

## Contact
For any questions or concerns, contact us at: [commande.cerise@priximportbgg.com](mailto:commande.cerise@priximportbgg.com)

<h2 id="privacy-policy">Privacy Policy</h2>

**Effective Date:** February 25, 2025

## Introduction
Cerise.ga respects the privacy of its users. This policy describes how we collect, use, and protect your personal data.

## Collected Data
We collect the following data through our Shopify platform:
* Name
* Email address
* Physical address
* Payment information
* IP address

## Use of Data
Your personal information is used for the following purposes:
* Delivery of ordered products
* Communication with users
* Targeted marketing
* Statistical analysis to improve our services

## Data Sharing
Your data may be shared with the following third-party services for advertising purposes:
* Shopify
* Meta (Facebook, Instagram)
* TikTok
* Google

We do not currently use any other third-party services, but we may do so in the future.

## Cookies
We only use the default cookies provided by Shopify to ensure the proper functioning of the site.

## Data Security
We use Shopify's built-in security measures to protect your personal information.

## User Rights
You can access, modify, or request the deletion of your personal data by logging into your account or contacting our customer service at:
📧 **commande.cerise@priximportbgg.com**

## Data Retention
Your personal data is retained for a maximum period of 90 days after you stop using our services.

## Children's Privacy
We do not knowingly collect or process personal data from individuals under the age of 16.

## Contact
For any questions regarding this policy, contact us at:
📧 **commande.cerise@priximportbgg.com**`,
  fr: `
<h2 id="terms-and-conditions">Conditions d'Utilisation</h2>

**Date d'entrée en vigueur :** 25 février 2025

## Acceptation
En utilisant cerise.ga, vous acceptez implicitement les présentes conditions d'utilisation.

## Description du service
Cerise.ga propose un service en ligne permettant aux utilisateurs de Libreville de commander des produits d'épicerie livrés en moins de 4 heures.

## Paiements et remboursements
Les paiements s'effectuent via notre processus de paiement en ligne standard. Les remboursements suivent la politique habituelle des applications e-commerce d'épicerie.

## Résiliation du compte
Cerise.ga se réserve le droit de suspendre ou résilier un compte utilisateur en cas de fraude ou d'activités suspectes.

## Propriété intellectuelle
Le site Cerise.ga et son contenu sont la propriété exclusive de Cerise.ga ou de ses fournisseurs.

## Lois applicables
Ces conditions sont régies exclusivement par les lois du Gabon.

## Modification des conditions
Cerise.ga se réserve le droit de modifier ces conditions d'utilisation à tout moment. Toute modification entrera immédiatement en vigueur dès publication sur notre site.

## Contact
Pour toute question ou préoccupation, contactez-nous à : [commande.cerise@priximportbgg.com](mailto:commande.cerise@priximportbgg.com)

<h2 id="privacy-policy">Politique de Confidentialité</h2>

**Date d'entrée en vigueur :** 25 février 2025

## Introduction
Cerise.ga respecte la confidentialité de ses utilisateurs. Cette politique décrit comment nous collectons, utilisons et protégeons vos données personnelles.

## Données collectées
Nous collectons les données suivantes via notre plateforme Shopify :
* Nom
* Adresse email
* Adresse physique
* Informations de paiement
* Adresse IP

## Utilisation des données
Vos informations personnelles sont utilisées aux fins suivantes :
* Livraison des produits commandés
* Communication avec les utilisateurs
* Marketing ciblé
* Analyses statistiques pour améliorer nos services

## Partage des données
Vos données peuvent être partagées avec les services tiers suivants pour des raisons publicitaires :
* Shopify
* Meta (Facebook, Instagram)
* TikTok
* Google

Nous n'utilisons actuellement aucun autre service tiers, mais nous pourrons le faire à l'avenir.

## Cookies
Nous utilisons uniquement les cookies par défaut fournis par Shopify pour assurer le bon fonctionnement du site.

## Sécurité des données
Nous utilisons les mesures de sécurité intégrées de Shopify pour protéger vos informations personnelles.

## Droits des utilisateurs
Vous pouvez accéder, modifier ou demander la suppression de vos données personnelles en vous connectant à votre compte ou en contactant notre service client à :
📧 **commande.cerise@priximportbgg.com**

## Conservation des données
Vos données personnelles sont conservées pendant une durée maximale de 90 jours après la fin de votre utilisation de nos services.

## Respect de la vie privée des mineurs
Nous ne collectons ni ne traitons sciemment les données personnelles de personnes âgées de moins de 16 ans.

## Contact
Pour toute question concernant cette politique, contactez-nous à :
📧 **commande.cerise@priximportbgg.com**`,
};

const LegalsCirclek = ({ handleLink }: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { isTablet } = useGlobal();

  useEffect(() => {
    setTimeout(() => {
      const anchor = window.location.hash.slice(1);
      handleLink(anchor);
    }, 0);
  }, []);

  const currentLanguage = i18n.language === 'fr' ? 'fr' : 'en';
  const content = termsContent[currentLanguage];

  return (
    <>
      <MobileHeaderLayout content={<MobileHeaderTitle text={t('legals')} />} />
      <div className="content-layout legals">
        <div className={styles.root}>
          {!isTablet && (
            <>
              <Breadcrumbs
                items={
                  [
                    {
                      text: t('home'),
                      link: '/',
                    },
                    {
                      text: t('legals'),
                      link: '/legals',
                    },
                  ].filter(Boolean) as BreadcrumbsItem[]
                }
              />

              <h1 className={styles.title}>{t('legals')}</h1>
            </>
          )}

          <div className={styles.page}>
            <div className={styles.page__links_container}>
              <ul className={styles.page__links}>
                {LEGALS_NAV.map((i) => (
                  <li className={styles.page__links_item} key={i.link}>
                    <a
                      href={`#${i.link}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLink(i.link);
                      }}
                    >
                      {i.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.page__content}>
              <ReactMarkdown
                className={mdStyles.mdStyles}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LegalsCirclek;
